/**
 * Amplifier profiles -- group 10.
 *
 * The organising axis of this group is *feedback*, because that is the single
 * design decision that most changes what an amplifier does to a signal. Two
 * entries (Ayre, darTZeel) use no global loop at all and therefore have an
 * output impedance high enough for the loudspeaker impedance curve to print
 * itself onto the response; two (Spectral, Goldmund) push the closed-loop
 * corner far outside the audio band and buy very low THD at the cost of a
 * high-order residue; one is a switching design whose output filter interacts
 * with the load; one is a brute-force high-current Class AB; one is a discrete
 * Class A line stage; and one is a zero-feedback directly-heated triode.
 *
 * Several of these manufacturers publish power and bandwidth but no distortion
 * figure. Where that is so the entry is ESTIMATED and the provenance note says
 * exactly which numbers were quoted.
 */

import type { AmpProfile } from '../../types/hardware';
import { HARMONIC_SHAPES, makeAmp, usd } from '../kit';

export const AMPS_10: AmpProfile[] = [
  makeAmp({
    mfr: 'Ayre Acoustics',
    model: 'MX-R Twenty',
    price: usd(32500, 'APPROXIMATE', 2016, 'Price per pair of monoblocks; recalled figure, not verified against a current dealer list.'),
    year: 2013,
    country: 'United States',
    topology: 'solid-state-ab',
    form: 'monoblock',
    devices: 'Fully balanced zero-feedback discrete output stage, Ayre Diamond circuit, machined single-billet chassis',
    power: { watts8: 300, watts4: 600, confidence: 'MANUFACTURER_SPEC' },
    gainDb: 26,
    thdPercent: 0.05,
    snrDb: 105,
    bandwidth: [1, 250000],
    outputImpedanceOhms: 0.15,
    feedbackDb: 0,
    crosstalkDb: -125,
    balancedInput: true,
    harmonicShape: HARMONIC_SHAPES.classA,
    nonlinear: { kind: 'jfet-square', amount: 0.5, asymmetry: 0.3, bias: 0.35, headroomDb: 12, oversample: 4 },
    dynamics: { sagDb: 0.3, sagTimeMs: 50, recoveryMs: 150, thermalDb: 0.15, thermalTimeMs: 7000, slewRateVus: 70, headroomDb: 2.2 },
    clipping: { kind: 'asymmetric', kneeDb: 3.5, stickiness: 0.1 },
    confidence: 'ESTIMATED',
    accent: '#9fb4cc',
    tags: ['zero-feedback', 'fully-balanced', 'diamond-output', 'separate-chassis'],
    notes:
      'A fully balanced output stage run with no global feedback loop, so the residual distortion is low-order and grows smoothly with drive rather than being suppressed to a fixed floor. The important system consequence is the source impedance: at roughly 0.15 ohm the damping factor is around fifty rather than several hundred, which is enough for a loudspeaker impedance curve with a 4 ohm dip and an 18 ohm bass peak to modulate the response by a few tenths of a decibel. Balanced monoblock construction puts the channels in separate chassis, so channel separation is limited by the source rather than the amplifier.',
    provenanceNote:
      'Power ratings, balanced zero-feedback topology and monoblock construction are from Ayre published material. Ayre does not publish a THD figure for this model, so distortion, output impedance, slew rate and SNR are estimates typical of a zero-feedback balanced Class AB output; the harmonic distribution is a topology-derived model, not a measurement.',
  }),

  makeAmp({
    mfr: 'Jeff Rowland Design Group',
    model: 'Continuum S2',
    price: usd(12000, 'APPROXIMATE', 2018, 'Recalled as approximately USD 12,000; not verified against a current dealer list.'),
    year: 2015,
    country: 'United States',
    topology: 'class-d',
    form: 'integrated',
    devices: 'Pascal-derived switching output modules with a Rowland discrete input and gain stage, transformer-isolated balanced input',
    power: { watts8: 400, watts4: 800, confidence: 'MANUFACTURER_SPEC' },
    gainDb: 34,
    thdPercent: 0.02,
    snrDb: 105,
    bandwidth: [5, 50000],
    dampingFactor: 400,
    feedbackDb: 30,
    crosstalkDb: -100,
    balancedInput: true,
    harmonicShape: HARMONIC_SHAPES.classD,
    nonlinear: { kind: 'class-d-pwm', amount: 0.35, carrierHz: 500000, headroomDb: 8, asymmetry: 0.12, oversample: 4 },
    dynamics: { sagDb: 0.12, sagTimeMs: 30, recoveryMs: 70, thermalDb: 0.08, slewRateVus: 22, headroomDb: 0.7 },
    clipping: { kind: 'hard', kneeDb: 0.35, stickiness: 0.05 },
    confidence: 'ESTIMATED',
    accent: '#6aa0c8',
    tags: ['switching-output', 'lc-output-filter', 'transformer-input', 'high-power-integrated'],
    notes:
      'A switching output stage followed by a passive LC filter that removes the carrier, which is the structural difference from every other entry in this group. That filter sits after the feedback take-off point in most module designs, so its corner interacts with the loudspeaker impedance and the top octave of the response becomes load-dependent in a way a linear amplifier is not. Supply behaviour is the mirror image of the valve entry below: essentially no sag under sustained load, but a hard clipping characteristic with almost no soft knee, and a distortion residue that is odd-order and rises with frequency rather than with level.',
    provenanceNote:
      'Power ratings, switching output topology and the transformer-coupled balanced input are from Jeff Rowland published material. THD, SNR, damping factor and bandwidth are estimates typical of a modern switching module of this power class; the carrier frequency and the load-dependent output-filter behaviour are modelled from the topology rather than quoted.',
  }),

  makeAmp({
    mfr: 'Spectral Audio',
    model: 'DMA-260 Series 2',
    price: usd(14000, 'APPROXIMATE', 2018, 'Recalled as approximately USD 14,000; Spectral sells through a restricted dealer network and the figure is not confirmed.'),
    year: 2014,
    country: 'United States',
    topology: 'solid-state-ab',
    form: 'stereo-power',
    devices: 'Wideband complementary bipolar output, DC-coupled, high global feedback, hybrid thick-film gain modules',
    power: { watts8: 180, watts4: 300, confidence: 'ESTIMATED' },
    gainDb: 26,
    thdPercent: 0.008,
    snrDb: 110,
    bandwidth: [1, 1000000],
    dampingFactor: 400,
    feedbackDb: 40,
    crosstalkDb: -100,
    balancedInput: true,
    harmonicShape: HARMONIC_SHAPES.highFeedback,
    nonlinear: { kind: 'class-b-crossover', amount: 0.3, crossoverWidth: 0.002, headroomDb: 12, asymmetry: 0.08, oversample: 4 },
    dynamics: { sagDb: 0.4, sagTimeMs: 55, recoveryMs: 130, thermalDb: 0.1, slewRateVus: 900, headroomDb: 1.6 },
    clipping: { kind: 'hard', kneeDb: 1.2, stickiness: 0.06 },
    confidence: 'ESTIMATED',
    accent: '#cbd3d8',
    tags: ['wide-bandwidth', 'high-feedback', 'high-slew-rate', 'cable-critical'],
    notes:
      'The design goal here is small-signal bandwidth measured in megahertz and a slew rate roughly an order of magnitude above a conventional audio amplifier, so that no part of the audio band is anywhere near the closed-loop corner. Large loop gain drives THD very low, but the distortion that survives is high-order rather than second-harmonic, which is the opposite residue structure from the zero-feedback entries in this group and the reason both are worth modelling separately. The stability margin depends on the reactance the output sees, which is why this design is specified with low-inductance cable; the profile reflects that as a high damping factor and a hard, narrow-knee clipping characteristic.',
    provenanceNote:
      'Wide-bandwidth DC-coupled topology, the very high slew rate and the cable requirement are from Spectral published material. The 8 and 4 ohm power figures, THD, SNR and damping factor are estimates for this model and are marked as such; Spectral does not publish a conventional full specification sheet. The high-order harmonic distribution is derived from the feedback topology.',
  }),

  makeAmp({
    mfr: 'darTZeel',
    model: 'NHB-108 model one',
    price: usd(28000, 'APPROXIMATE', 2015, 'Recalled as approximately USD 28,000; the price moved substantially over the model life and this figure is not verified.'),
    year: 2003,
    country: 'Switzerland',
    topology: 'solid-state-ab',
    form: 'stereo-power',
    devices: 'Zero global feedback single-stage voltage amplifier with bipolar output followers, 50 ohm BNC line interface',
    power: { watts8: 100, watts4: 160, confidence: 'MANUFACTURER_SPEC' },
    gainDb: 26,
    thdPercent: 0.15,
    snrDb: 96,
    bandwidth: [2, 200000],
    outputImpedanceOhms: 0.28,
    feedbackDb: 0,
    crosstalkDb: -80,
    balancedInput: false,
    harmonicShape: HARMONIC_SHAPES.classA,
    nonlinear: { kind: 'jfet-square', amount: 0.6, asymmetry: 0.55, bias: 0.6, headroomDb: 10, oversample: 4 },
    dynamics: { sagDb: 0.6, sagTimeMs: 70, recoveryMs: 180, thermalDb: 0.2, slewRateVus: 45, headroomDb: 1.4 },
    clipping: { kind: 'asymmetric', kneeDb: 5, stickiness: 0.15 },
    confidence: 'ESTIMATED',
    accent: '#d4b23c',
    tags: ['zero-feedback', 'bnc-50-ohm-interface', 'single-ended-input', 'low-damping'],
    notes:
      'No global feedback and a deliberately short signal path, which leaves a distortion figure roughly an order of magnitude above the high-feedback designs here but with almost all of it in the second harmonic and rising about a decibel per decibel of drive. Output impedance near 0.3 ohm gives a damping factor under thirty, so this amplifier and a 4 ohm speaker with a lively impedance curve produce a measurably different frequency response than the same speaker on the Karan entry below -- the engine computes that interaction rather than baking it into either profile. The proprietary 50 ohm BNC interface is a current-mode rather than voltage-mode link between preamp and power amp, which is why the single-ended input path is treated as the design intent rather than a limitation.',
    provenanceNote:
      'Power rating, the absence of global feedback and the 50 ohm BNC interface are from darTZeel published material. THD, SNR, output impedance and bandwidth are estimated from the stated zero-feedback topology and from period measurements of comparable designs; darTZeel does not publish a complete distortion specification.',
  }),

  makeAmp({
    mfr: 'Goldmund',
    model: 'Telos 590 NextGen II',
    price: usd(40000, 'APPROXIMATE', 2019, 'Recalled as approximately USD 40,000; Goldmund pricing varies by market and the figure is not verified.'),
    year: 2017,
    country: 'Switzerland',
    topology: 'solid-state-ab',
    form: 'integrated',
    devices: 'Goldmund wideband output modules on a mechanically grounded chassis, with an integral DSP input stage',
    power: { watts8: 215, watts4: 300, confidence: 'ESTIMATED' },
    gainDb: 36,
    thdPercent: 0.02,
    snrDb: 105,
    bandwidth: [1, 500000],
    dampingFactor: 300,
    feedbackDb: 26,
    crosstalkDb: -95,
    balancedInput: true,
    nonlinear: { kind: 'class-b-crossover', amount: 0.35, crossoverWidth: 0.0025, headroomDb: 11, asymmetry: 0.12, oversample: 4 },
    dynamics: { sagDb: 0.45, sagTimeMs: 60, recoveryMs: 140, thermalDb: 0.12, slewRateVus: 400, headroomDb: 1.5 },
    clipping: { kind: 'asymmetric', kneeDb: 2.0, stickiness: 0.07 },
    confidence: 'ESTIMATED',
    accent: '#8d8f96',
    tags: ['wide-bandwidth', 'mechanical-grounding', 'moderate-feedback', 'high-power-integrated'],
    notes:
      'The stated objective is minimum phase error inside the audio band, achieved by placing the closed-loop corner several hundred kilohertz out so that the phase lag at 20 kHz is a fraction of a degree; the profile carries that as a very wide derived bandwidth and a slew rate several times a conventional Class AB design. Feedback is substantial but not extreme, so the harmonic residue sits between the Spectral entry and the zero-feedback designs: mostly low-order, with a narrow crossover region from a well-biased output stage. Mechanical grounding of the chassis is a microphony and resonance measure rather than an electrical one, so it does not appear in the electrical model at all -- noted here so the omission is deliberate rather than an oversight.',
    provenanceNote:
      'The wide-bandwidth design philosophy, mechanical grounding and integrated form are from Goldmund published material. Power ratings, THD, SNR, damping factor and slew rate are estimates for this model; Goldmund publishes bandwidth and phase claims but not a conventional distortion specification, so the harmonic structure is topology-derived.',
  }),

  makeAmp({
    mfr: 'Karan Acoustics',
    model: 'KA M 2000',
    price: usd(55000, 'APPROXIMATE', 2018, 'Price per pair of monoblocks; recalled approximately and not verified against a current dealer list.'),
    year: 2010,
    country: 'Serbia',
    topology: 'solid-state-ab',
    form: 'monoblock',
    devices: 'Large paralleled complementary bipolar output bank with very large reservoir capacitance and a dual-mono supply',
    power: { watts8: 2000, watts4: 3000, confidence: 'ESTIMATED' },
    gainDb: 29,
    thdPercent: 0.03,
    snrDb: 110,
    bandwidth: [1, 300000],
    dampingFactor: 1200,
    feedbackDb: 28,
    crosstalkDb: -130,
    balancedInput: true,
    nonlinear: { kind: 'class-b-crossover', amount: 0.5, crossoverWidth: 0.006, headroomDb: 9, asymmetry: 0.18, oversample: 4 },
    dynamics: { sagDb: 0.12, sagTimeMs: 400, recoveryMs: 900, thermalDb: 0.1, thermalTimeMs: 12000, slewRateVus: 120, headroomDb: 3.0 },
    clipping: { kind: 'asymmetric', kneeDb: 2.5, stickiness: 0.05 },
    confidence: 'ESTIMATED',
    accent: '#4f6d8c',
    tags: ['high-current', 'large-reservoir', 'high-damping', 'monoblock'],
    notes:
      'A very large output device bank and an unusually large reservoir bank, which together mean the supply barely moves under sustained full-power load; the model gives it a small sag figure with a long time constant instead of the short, deep sag of a compact amplifier. Damping factor above a thousand flattens loudspeaker impedance interaction almost completely, so a speaker whose response varies on the darTZeel entry will measure essentially flat here. The cost of paralleling many output devices is a wider effective crossover region, so at low level the residual distortion is higher-order than a Class A design of a tenth the power, even though the absolute THD figure at rated output is lower.',
    provenanceNote:
      'Monoblock construction, the dual-mono supply and the very large output and reservoir banks are from Karan Acoustics published material. The model designation reflects the rated 8 ohm power; the 4 ohm figure, THD, SNR, damping factor and bandwidth are inferred from the design class and are not quoted specifications.',
  }),

  makeAmp({
    mfr: 'Robert Koda',
    model: 'Takumi K-15EX',
    price: usd(32000, 'APPROXIMATE', 2018, 'Recalled as approximately USD 32,000; Robert Koda builds to order and the figure is not verified.'),
    year: 2015,
    country: 'Japan',
    topology: 'solid-state-a',
    form: 'preamp',
    devices: 'Discrete Class A symmetrical gain stage with no global feedback, hand-built point-to-point signal path',
    power: { outputVrms: 12, confidence: 'ESTIMATED' },
    gainDb: 12,
    thdPercent: 0.03,
    snrDb: 105,
    bandwidth: [1, 400000],
    outputImpedanceOhms: 40,
    feedbackDb: 0,
    crosstalkDb: -90,
    balancedInput: false,
    harmonicShape: HARMONIC_SHAPES.classA,
    nonlinear: { kind: 'jfet-square', amount: 0.45, asymmetry: 0.45, bias: 0.6, headroomDb: 16, oversample: 4 },
    dynamics: { sagDb: 0.05, sagTimeMs: 40, recoveryMs: 120, thermalDb: 0.3, thermalTimeMs: 9000, slewRateVus: 90, headroomDb: 6 },
    clipping: { kind: 'asymmetric', kneeDb: 6, stickiness: 0.08 },
    confidence: 'ESTIMATED',
    accent: '#6b5b4a',
    tags: ['line-stage', 'zero-feedback', 'class-a', 'low-gain'],
    notes:
      'A line stage rather than a power amplifier, so the parameters that matter are gain, output impedance and headroom rather than watts and damping factor. Twelve decibels of gain with roughly twenty-two volts of clipping headroom means this stage is operating around forty decibels below its limit at normal listening levels, which is why its modelled harmonic contribution is small but not zero and is second-harmonic weighted from the zero-feedback discrete topology. Forty ohms of source impedance is low enough that cable capacitance is irrelevant, but the thermal drift of a Class A stage is given a longer time constant than the power amplifiers here because its dissipation is small and constant.',
    provenanceNote:
      'Zero-feedback discrete Class A topology and the line-stage form are from Robert Koda published material. Robert Koda does not publish THD, SNR, gain or output impedance for this model, so all four are estimates derived from the topology; the nominal output voltage is a modelling assumption rather than a rated figure.',
  }),

  makeAmp({
    mfr: 'Absolare',
    model: 'Passion Integrated',
    price: usd(42000, 'APPROXIMATE', 2018, 'Recalled as approximately USD 42,000; Absolare builds to order with leather and finish options that move the price, so treat this as indicative.'),
    year: 2013,
    country: 'Turkey',
    topology: 'tube-set',
    form: 'integrated',
    devices: '845 directly-heated triode single-ended output, driver triode, zero feedback, custom output transformers',
    power: { watts8: 25, watts4: 16, confidence: 'ESTIMATED' },
    gainDb: 32,
    thdPercent: 1.0,
    snrDb: 82,
    bandwidth: [16, 30000],
    outputImpedanceOhms: 2.8,
    feedbackDb: 0,
    outputTransformer: true,
    transformerLfHz: 20,
    transformerHfHz: 27000,
    crosstalkDb: -55,
    balancedInput: false,
    harmonicShape: HARMONIC_SHAPES.set,
    nonlinear: { kind: 'triode', amount: 0.9, asymmetry: 0.8, bias: 0.85, headroomDb: 8, oversample: 8 },
    dynamics: { sagDb: 1.9, sagTimeMs: 230, recoveryMs: 560, thermalDb: 0.2, slewRateVus: 15, headroomDb: 1.0 },
    clipping: { kind: 'soft', kneeDb: 9, stickiness: 0.38 },
    confidence: 'ESTIMATED',
    accent: '#a5502e',
    tags: ['845-triode', 'single-ended', 'zero-feedback', 'output-transformer'],
    notes:
      'A directly-heated triode operating single-ended with no feedback loop, so distortion is dominated by a second harmonic that rises roughly a decibel per decibel of drive and reaches around one percent at rated output. Nearly 3 ohms of source impedance means the loudspeaker impedance curve is superimposed on the frequency response almost one for one: a speaker with an 18 ohm bass peak and a 5 ohm midrange minimum will show a bass lift of over a decibel with this amplifier and none with the Karan monoblock. The output transformer adds two more mechanisms the solid-state entries do not have -- first-order corners at roughly 20 Hz and 27 kHz, and core hysteresis that lifts low-order harmonics at low frequency and high level -- while the supply sags nearly 2 dB on sustained peaks and recovers over half a second.',
    provenanceNote:
      'Single-ended 845 topology, zero feedback and the custom output transformers are from Absolare published material. Absolare does not publish THD, SNR, output impedance or transformer corner frequencies, so all of those are estimated from the topology and from measurements of comparable 845 single-ended designs. Power figures are approximate and the 4 ohm rating in particular is inferred.',
  }),
];
